// Undo/redo over whole resume snapshots. Keystrokes into the same field
// within a short burst collapse into one step, the way Word undoes a word
// at a time rather than a letter.

import { useCallback, useRef, useState } from "react";
import type { AgpResume } from "@agp/shared";

const MAX_STEPS = 200;
const BURST_MS = 800;

export function useResumeHistory(initial: AgpResume | null) {
  const [resume, setResumeState] = useState<AgpResume | null>(initial);
  const past = useRef<AgpResume[]>([]);
  const future = useRef<AgpResume[]>([]);
  const lastPush = useRef<{ key: string | null; at: number }>({ key: null, at: 0 });
  const current = useRef<AgpResume | null>(initial);
  const [, bump] = useState(0);

  /** Replaces the resume without recording history (initial load, server
   *  round-trips). */
  const reset = useCallback((next: AgpResume | null) => {
    current.current = next;
    past.current = [];
    future.current = [];
    setResumeState(next);
  }, []);

  /** Applies an edit. `key` names the field being typed into so a burst of
   *  keystrokes there becomes one undo step. */
  const apply = useCallback((next: AgpResume, key: string | null = null) => {
    const prev = current.current;
    const now = Date.now();
    const sameBurst = key !== null && key === lastPush.current.key && now - lastPush.current.at < BURST_MS;
    if (prev && !sameBurst) {
      past.current.push(prev);
      if (past.current.length > MAX_STEPS) past.current.shift();
    }
    lastPush.current = { key, at: now };
    future.current = [];
    current.current = next;
    setResumeState(next);
  }, []);

  const undo = useCallback((): AgpResume | null => {
    const prev = past.current.pop();
    if (!prev || !current.current) return null;
    future.current.push(current.current);
    lastPush.current = { key: null, at: 0 };
    current.current = prev;
    setResumeState(prev);
    bump((n) => n + 1);
    return prev;
  }, []);

  const redo = useCallback((): AgpResume | null => {
    const next = future.current.pop();
    if (!next || !current.current) return null;
    past.current.push(current.current);
    lastPush.current = { key: null, at: 0 };
    current.current = next;
    setResumeState(next);
    bump((n) => n + 1);
    return next;
  }, []);

  return {
    resume,
    apply,
    reset,
    undo,
    redo,
    canUndo: past.current.length > 0,
    canRedo: future.current.length > 0,
  };
}
